import { useState } from "react";
import { validateBbWcRate } from "./formValidation";
import { useBbWcToggle } from "./useBbWcToggle";
import { useRateBands, type RateBandsState } from "./useRateBands";

export interface BbWcRatesState {
  bbWcRateFallback: boolean;
  bbWcRateAlways: boolean;
  bbWcLandingRate: string;
  bbWcLandingRateError: string | null;
  /** Parsed, or null while disabled or invalid. Feeds {@link formValidation.ts}'s completeness check. */
  bbWcParsedLandingRate: number | null;
  bbWcBands: RateBandsState;
  bbWcCustomsFallback: boolean;
  bbWcCustomsAlways: boolean;
  bbWcCustomsRate: string;
  bbWcCustomsRateError: string | null;
  /** Parsed, or null while disabled or invalid. */
  bbWcParsedCustomsRate: number | null;
  setBbWcRateFallback: (value: boolean) => void;
  setBbWcRateAlways: (value: boolean) => void;
  setBbWcLandingRate: (value: string) => void;
  setBbWcCustomsFallback: (value: boolean) => void;
  setBbWcCustomsAlways: (value: boolean) => void;
  setBbWcCustomsRate: (value: string) => void;
}

/**
 * State for the optional Boot Barn wholesale landing and customs rates.
 *
 * BB WC Shipments clears through a different port of entry than the rest of
 * the tracker, so it can take its own quoted rate for either figure,
 * independently of the other. Both pairs start off, and off is meaningful: a
 * run that never touches this card posts exactly the request it always did.
 *
 * Split out of `useUploadForm.ts` so that hook stays wiring-only; the coupling
 * rule itself lives in {@link ./useBbWcToggle.ts} and the rate check in
 * {@link ./formValidation.ts}.
 *
 * @param seedMonth Passed through to the dated-rate editor, same as the main
 *   `useRateBands` instance next to it.
 */
export function useBbWcRates(seedMonth?: string): BbWcRatesState {
  const rate = useBbWcToggle();
  const [landingRateInput, setLandingRateInput] = useState("");
  const bands = useRateBands(seedMonth);

  const customs = useBbWcToggle();
  const [customsRateInput, setCustomsRateInput] = useState("");

  const { rate: parsedLandingRate, error: landingRateError } = validateBbWcRate(
    rate.fallback || rate.always,
    landingRateInput,
  );
  const { rate: parsedCustomsRate, error: customsRateError } = validateBbWcRate(
    customs.fallback || customs.always,
    customsRateInput,
  );

  return {
    bbWcRateFallback: rate.fallback,
    bbWcRateAlways: rate.always,
    bbWcLandingRate: landingRateInput,
    bbWcLandingRateError: landingRateError,
    bbWcParsedLandingRate: parsedLandingRate,
    bbWcBands: bands,
    bbWcCustomsFallback: customs.fallback,
    bbWcCustomsAlways: customs.always,
    bbWcCustomsRate: customsRateInput,
    bbWcCustomsRateError: customsRateError,
    bbWcParsedCustomsRate: parsedCustomsRate,
    setBbWcRateFallback: rate.setFallback,
    setBbWcRateAlways: rate.setAlways,
    setBbWcLandingRate: setLandingRateInput,
    setBbWcCustomsFallback: customs.setFallback,
    setBbWcCustomsAlways: customs.setAlways,
    setBbWcCustomsRate: setCustomsRateInput,
  };
}
